import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    bridgeStatusQueryKey,
    doctorMappingQueryKey,
    fetchBridgeStatus,
    fetchDoctorMapping,
} from "@/common/lib/api/bridge";
import { useAccountContext } from "@providers/AccountProvider";
import DoctorMappingModal from "@/features/settings/components/integrations/DoctorMappingModal";

/**
 * EzyvetDoctorMerge
 *
 * ezyVet sync matches doctors only by ezyVet resource id, so a doctor added in HappyDoc before the
 * sync (or one from the old user-id sync) ends up beside a new synced row for the same vet. This
 * offers the EN-997 mapping modal in its ezyVet mode, where a person merges each synced provider
 * into the existing doctor (which keeps its visits). Nothing merges without that choice.
 * Spec: docs/superpowers/specs/2026-10-14-ezyvet-bridge-doctor-merge-design.md
 */
export default function EzyvetDoctorMerge() {
    const { accountData } = useAccountContext();
    const accountId = accountData?.id ? String(accountData.id) : undefined;
    const [open, setOpen] = useState(false);

    // Same cache entry BridgeStatus reads, so this is a second observer, not a second request.
    const { data: status } = useQuery({
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        staleTime: 60_000,
    });
    const isEzyvet = !!status?.installed && status.is_active !== false && status.module_type === "ezyvet";
    const { data: mapping } = useQuery({
        queryKey: doctorMappingQueryKey(accountId),
        queryFn: ({ queryKey }) => fetchDoctorMapping(queryKey[1] as string),
        enabled: isEzyvet && !!accountId,
    });

    if (!isEzyvet || !mapping || mapping.oldDoctors.length === 0 || mapping.bridgeDoctors.length === 0) return null;
    const likely = mapping.oldDoctors.filter((d) => d.candidates.length > 0).length;
    const text =
        likely === 0
            ? "Doctors added in HappyDoc before ezyVet sync can be merged with their ezyVet provider."
            : likely === 1
              ? "1 existing doctor may duplicate synced ezyVet providers."
              : `${likely} existing doctors may duplicate synced ezyVet providers.`;

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700">{text}</p>
            <button type="button" className="secondary-button" onClick={() => setOpen(true)}>
                Match existing doctors
            </button>
            <DoctorMappingModal accountId={accountId} open={open} onClose={() => setOpen(false)} />
        </div>
    );
}
